import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Shield, Target, Clock, Award, ChevronLeft, ChevronRight, 
  Mail, Users, Lock, Server, Globe, Monitor, PlayCircle,
  Star, Trophy, BookOpen, Zap, AlertCircle
} from 'lucide-react';

const ChallengeOverview = ({ apiService, userProfile, gamificationEngine }) => {
  const { challengeSetId } = useParams(); // e.g., 'phishing-email-detection'
  const navigate = useNavigate();
  const location = useLocation();
  const [challengeSet, setChallengeSet] = useState(null);
  const [subChallenges, setSubChallenges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Parse URL parameters for arena context
  const queryParams = new URLSearchParams(location.search);
  const arenaId = queryParams.get('arena');
  const topicFilter = queryParams.get('topic');

  // Challenge set definitions with their sub-challenges
  const challengeSetData = {
    'phishing-email-detection': {
      id: 'phishing-email-detection',
      title: 'Phishing Email Detection',
      description: 'Master the art of identifying and defending against phishing attacks through realistic email scenarios',
      category: 'phishing',
      difficulty: 'Beginner to Advanced',
      totalChallenges: 4,
      estimatedTime: '25-35 min',
      xpReward: '200-800 XP',
      icon: Mail,
      color: 'blue',
      videoUrl: '/assets/videos/phishing-overview.mp4',
      promoImageUrl: '/assets/images/phishing-arena.jpg',
      learningObjectives: [
        'Identify suspicious sender addresses and email headers',
        'Recognize urgency tactics and social engineering techniques', 
        'Analyze links and attachments for malicious content',
        'Understand email authentication mechanisms',
        'Apply best practices for email security'
      ],
      skillsGained: [
        'Email Security Analysis',
        'Threat Detection',
        'Social Engineering Defense',
        'Critical Thinking',
        'Risk Assessment'
      ],
      subChallenges: [
        {
          id: 'basic-phishing-identification',
          title: 'Basic Phishing Identification',
          description: 'Learn to spot obvious phishing emails with clear warning signs',
          difficulty: 'Beginner',
          estimatedTime: '5-8 min',
          xpReward: 150,
          order: 1
        },
        {
          id: 'advanced-spear-phishing',
          title: 'Advanced Spear Phishing',
          description: 'Identify sophisticated, targeted phishing attempts',
          difficulty: 'Intermediate',
          estimatedTime: '8-12 min',
          xpReward: 200,
          order: 2
        },
        {
          id: 'business-email-compromise',
          title: 'Business Email Compromise',
          description: 'Detect CEO fraud and invoice scam attempts',
          difficulty: 'Advanced',
          estimatedTime: '10-15 min',
          xpReward: 300,
          order: 3
        },
        {
          id: 'email-header-analysis',
          title: 'Email Header Analysis',
          description: 'Deep dive into technical email analysis and verification',
          difficulty: 'Advanced',
          estimatedTime: '12-18 min',
          xpReward: 300,
          order: 4
        }
      ]
    },
    'social-engineering-scenarios': {
      id: 'social-engineering-scenarios',
      title: 'Social Engineering Defense',
      description: 'Learn to recognize and defend against various social engineering attacks',
      category: 'social-engineering',
      difficulty: 'Intermediate',
      totalChallenges: 3,
      estimatedTime: '20-30 min',
      xpReward: '300-600 XP',
      icon: Users,
      color: 'purple',
      videoUrl: '/assets/videos/social-engineering-overview.mp4',
      promoImageUrl: '/assets/images/social-engineering-arena.jpg',
      learningObjectives: [
        'Understand psychological manipulation techniques',
        'Recognize pretexting and impersonation attacks',
        'Apply verification protocols for suspicious requests',
        'Handle phone-based social engineering attempts',
        'Implement organizational security awareness'
      ],
      skillsGained: [
        'Social Engineering Defense',
        'Psychological Awareness',
        'Verification Protocols',
        'Communication Security',
        'Risk Evaluation'
      ],
      subChallenges: [
        {
          id: 'pretexting-phone-calls',
          title: 'Pretexting Phone Call Defense',
          description: 'Handle realistic pretexting scenarios over the phone',
          difficulty: 'Intermediate',
          estimatedTime: '8-12 min',
          xpReward: '150-250 XP',
          order: 1
        },
        {
          id: 'authority-impersonation',
          title: 'Authority Impersonation Scenarios',
          description: 'Identify when someone is impersonating authority figures',
          difficulty: 'Advanced',
          estimatedTime: '10-15 min',
          xpReward: '200-300 XP',
          order: 2
        },
        {
          id: 'information-harvesting-defense',
          title: 'Information Harvesting Defense',
          description: 'Protect against attackers gathering personal information',
          difficulty: 'Intermediate',
          estimatedTime: '8-12 min',
          xpReward: '150-280 XP',
          order: 3
        }
      ]
    },
    'password-strength-analysis': {
      id: 'password-strength-analysis',
      title: 'Password Security Mastery',
      description: 'Comprehensive training on password security and authentication best practices',
      category: 'password-security',
      difficulty: 'Beginner to Advanced',
      totalChallenges: 3,
      estimatedTime: '15-25 min',
      xpReward: '200-500 XP',
      icon: Lock,
      color: 'green',
      videoUrl: '/assets/videos/password-security-overview.mp4',
      promoImageUrl: '/assets/images/password-arena.jpg',
      learningObjectives: [
        'Create and evaluate strong passwords',
        'Understand password entropy and complexity',
        'Implement multi-factor authentication',
        'Use password managers effectively',
        'Handle password-related security incidents'
      ],
      skillsGained: [
        'Password Management',
        'Authentication Security',
        'Security Tools Usage',
        'Risk Assessment',
        'Security Policy Implementation'
      ],
      subChallenges: [
        {
          id: 'password-creation-best-practices',
          title: 'Password Creation Best Practices',
          description: 'Learn to create strong, memorable passwords',
          difficulty: 'Beginner',
          estimatedTime: '5-8 min',
          xpReward: '80-150 XP',
          order: 1
        },
        {
          id: 'password-manager-implementation',
          title: 'Password Manager Implementation',
          description: 'Set up and use password managers effectively',
          difficulty: 'Intermediate',
          estimatedTime: '8-12 min',
          xpReward: '120-200 XP',
          order: 2
        },
        {
          id: 'multi-factor-authentication-setup',
          title: 'Multi-Factor Authentication Setup',
          description: 'Implement and manage various MFA methods',
          difficulty: 'Advanced',
          estimatedTime: '10-15 min',
          xpReward: '150-300 XP',
          order: 3
        }
      ]
    }
  };

  useEffect(() => {
    const fetchChallengeSetData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // First try to get challenge set data from the predefined sets
        const challengeSetInfo = challengeSetData[challengeSetId];
        
        if (challengeSetInfo) {
          setChallengeSet(challengeSetInfo);
          setSubChallenges(challengeSetInfo.subChallenges);
        } else {
          // Fallback: try to fetch from API or generate generic
          if (apiService) {
            try {
              const response = await apiService.get(`/challenge-sets/${challengeSetId}`);
              if (response.success && response.data) {
                setChallengeSet(response.data);
                setSubChallenges(response.data.subChallenges || []);
              } else {
                throw new Error('Challenge set not found');
              }
            } catch (apiError) {
              console.warn('API fetch failed, using fallback:', apiError);
              // Generate a generic challenge set
              const genericSet = generateGenericChallengeSet(challengeSetId);
              setChallengeSet(genericSet);
              setSubChallenges(genericSet.subChallenges);
            }
          } else {
            // Generate a generic challenge set when no API is available
            const genericSet = generateGenericChallengeSet(challengeSetId);
            setChallengeSet(genericSet);
            setSubChallenges(genericSet.subChallenges);
          }
        }
      } catch (error) {
        console.error('Error fetching challenge set:', error);
        setError(`Failed to load challenge set: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (challengeSetId) {
      fetchChallengeSetData();
    }
  }, [challengeSetId, apiService]);

  const generateGenericChallengeSet = (setId) => {
    return {
      id: setId,
      title: 'Security Challenge Set',
      description: 'A comprehensive security training challenge set',
      category: 'general',
      difficulty: 'Beginner to Advanced',
      totalChallenges: 3,
      estimatedTime: '15-25 min',
      xpReward: '200-400 XP',
      icon: Shield,
      color: 'blue',
      videoUrl: '/assets/videos/default-challenge.mp4',
      promoImageUrl: '/assets/images/next-challenge.jpg',
      learningObjectives: [
        'Complete security assessments',
        'Demonstrate cybersecurity knowledge',
        'Apply security best practices'
      ],
      skillsGained: [
        'Security Awareness',
        'Risk Assessment',
        'Best Practices'
      ],
      subChallenges: [
        {
          id: `${setId}-basic`,
          title: 'Basic Security Knowledge',
          description: 'Test your fundamental security understanding',
          difficulty: 'Beginner',
          estimatedTime: '5-8 min',
          xpReward: '80-120 XP',
          order: 1
        },
        {
          id: `${setId}-intermediate`,
          title: 'Intermediate Security Scenarios',
          description: 'Apply security knowledge to realistic scenarios',
          difficulty: 'Intermediate',
          estimatedTime: '8-12 min',
          xpReward: '120-200 XP',
          order: 2
        },
        {
          id: `${setId}-advanced`,
          title: 'Advanced Security Analysis',
          description: 'Demonstrate advanced security analysis skills',
          difficulty: 'Advanced',
          estimatedTime: '10-15 min',
          xpReward: '150-250 XP',
          order: 3
        }
      ]
    };
  };

  const handleStartChallengeSet = () => {
    // Start with the first sub-challenge
    if (subChallenges.length > 0) {
      const firstChallenge = subChallenges[0];
      const challengeUrl = arenaId && topicFilter
        ? `/challenge-attempt/${firstChallenge.id}?arena=${arenaId}&topic=${topicFilter}&challengeSet=${challengeSetId}`
        : arenaId
        ? `/challenge-attempt/${firstChallenge.id}?arena=${arenaId}&challengeSet=${challengeSetId}`
        : `/challenge-attempt/${firstChallenge.id}?challengeSet=${challengeSetId}`;
      
      navigate(challengeUrl);
    }
  };

  const handleSubChallengeClick = (subChallenge) => {
    const challengeUrl = arenaId && topicFilter
      ? `/challenge-attempt/${subChallenge.id}?arena=${arenaId}&topic=${topicFilter}&challengeSet=${challengeSetId}`
      : arenaId
      ? `/challenge-attempt/${subChallenge.id}?arena=${arenaId}&challengeSet=${challengeSetId}`
      : `/challenge-attempt/${subChallenge.id}?challengeSet=${challengeSetId}`;
    
    navigate(challengeUrl);
  };

  const handleBackToArena = () => {
    if (arenaId) {
      const arenaUrl = topicFilter 
        ? `/challenges?arena=${arenaId}&topic=${topicFilter}`
        : `/challenges?arena=${arenaId}`;
      navigate(arenaUrl);
    } else {
      navigate('/challenges');
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner': return 'text-green-400 bg-green-900/50';
      case 'intermediate': return 'text-yellow-400 bg-yellow-900/50';  
      case 'advanced': return 'text-red-400 bg-red-900/50';
      default: return 'text-gray-400 bg-gray-700';
    }
  };

  const getColorStyles = (color) => {
    const colorStyles = {
      blue: {
        gradient: 'from-blue-600 to-blue-800',
        accent: 'text-blue-400',
        bg: 'bg-blue-500/20',
        border: 'border-blue-500/50'
      },
      purple: {
        gradient: 'from-purple-600 to-purple-800',
        accent: 'text-purple-400',
        bg: 'bg-purple-500/20',
        border: 'border-purple-500/50'
      },
      green: {
        gradient: 'from-green-600 to-green-800',
        accent: 'text-green-400',
        bg: 'bg-green-500/20',
        border: 'border-green-500/50'
      },
      red: {
        gradient: 'from-red-600 to-red-800',
        accent: 'text-red-400',
        bg: 'bg-red-500/20',
        border: 'border-red-500/50'
      }
    };
    return colorStyles[color] || colorStyles.blue;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading challenge overview...</p>
        </div>
      </div>
    );
  }

  if (!challengeSet) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <AlertCircle size={48} className="mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-bold mb-2">Challenge Set Not Found</h2>
          <p className="text-gray-400 mb-4">The requested challenge set could not be loaded.</p>
          <button 
            onClick={handleBackToArena}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            {arenaId ? 'Back to Arena' : 'Back to Challenges'}
          </button>
        </div>
      </div>
    );
  }

  const IconComponent = challengeSet.icon || Shield;
  const styles = getColorStyles(challengeSet.color);

  return (
    <div className="min-h-screen bg-gray-900 text-white p-3 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
          <button 
            onClick={handleBackToArena}
            className="w-full sm:w-auto flex items-center justify-center sm:justify-start text-gray-400 hover:text-white transition-colors p-3 bg-gray-800/50 rounded-lg border border-gray-700"
          >
            <ChevronLeft size={20} className="mr-2" />
            {arenaId ? 'Back to Arena' : 'Back to Challenges'}
          </button>
          
          {error && (
            <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg px-4 py-2">
              <div className="flex items-center text-yellow-400 text-sm">
                <AlertCircle size={16} className="mr-2" />
                Using fallback data
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 lg:gap-8">
          {/* Left Column - Overview */}
          <div className="xl:col-span-2 space-y-6 sm:space-y-8">
            {/* Challenge Set Header */}
            <div className="bg-gray-800/50 rounded-lg p-4 sm:p-6 border border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-start mb-6 gap-4">
                <div className={`p-4 ${styles.bg} rounded-lg sm:mr-2`}>
                  <IconComponent size={32} className={styles.accent} />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl sm:text-3xl font-bold mb-2">{challengeSet.title}</h1>
                  <p className="text-gray-400 text-base sm:text-lg mb-4">{challengeSet.description}</p>
                  <div className="flex flex-wrap items-center gap-3 sm:gap-6">
                    <div className="flex items-center text-gray-400">
                      <Trophy size={16} className="mr-2" />
                      <span>{challengeSet.totalChallenges} Challenges</span>
                    </div>
                    <div className="flex items-center text-gray-400">
                      <Clock size={16} className="mr-2" />
                      <span>{challengeSet.estimatedTime}</span>
                    </div>
                    <div className="flex items-center text-purple-400">
                      <Award size={16} className="mr-2" />
                      <span>{challengeSet.xpReward}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Video */}
              {challengeSet.videoUrl && (
                <div className="mb-6">
                  <div className="bg-black rounded-lg overflow-hidden border border-gray-700 h-48 sm:h-64">
                    <iframe 
                      className="w-full h-full" 
                      src={challengeSet.videoUrl} 
                      title="Challenge Set Overview" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              )}

              {/* Learning Objectives */}
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Target size={22} className={`mr-3 ${styles.accent}`} />
                  Learning Objectives
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {challengeSet.learningObjectives.map((objective, index) => (
                    <div key={index} className="flex items-start">
                      <ChevronRight size={16} className={`mr-3 mt-1 ${styles.accent}`} />
                      <span className="text-gray-300">{objective}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Skills Gained */}
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Zap size={22} className={`mr-3 ${styles.accent}`} />
                  Skills You'll Gain
                </h3>
                <div className="flex flex-wrap gap-2">
                  {challengeSet.skillsGained.map((skill, index) => (
                    <span 
                      key={index} 
                      className={`px-3 py-2 ${styles.bg} ${styles.border} border rounded-full text-sm font-medium`}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Sub-Challenges List */}
            <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <BookOpen size={24} className={`mr-3 ${styles.accent}`} />
                Challenge Sequence
              </h2>
              
              <div className="space-y-4">
                {subChallenges.map((subChallenge, index) => (
                  <div 
                    key={subChallenge.id}
                    onClick={() => handleSubChallengeClick(subChallenge)}
                    className="bg-gray-700/50 rounded-lg p-4 border border-gray-600 hover:border-gray-500 cursor-pointer transition-all group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start min-w-0">
                        <div className={`w-8 h-8 ${styles.bg} rounded-full flex items-center justify-center mr-4`}>
                          <span className={`font-bold ${styles.accent}`}>{subChallenge.order}</span>
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-lg group-hover:text-blue-300 transition-colors">
                            {subChallenge.title}
                          </h3>
                          <p className="text-gray-400 text-sm mb-2">{subChallenge.description}</p>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm">
                            <span className={`px-2 py-1 rounded-full ${getDifficultyColor(subChallenge.difficulty)}`}>
                              {subChallenge.difficulty}
                            </span>
                            <div className="flex items-center text-gray-500">
                              <Clock size={14} className="mr-1" />
                              {subChallenge.estimatedTime}
                            </div>
                            <div className="flex items-center text-purple-400">
                              <Award size={14} className="mr-1" />
                              {subChallenge.xpReward}
                            </div>
                          </div>
                        </div>
                      </div>
                      <ChevronRight size={20} className="text-gray-400 group-hover:text-white transition-colors shrink-0 mt-1" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Action Panel */}
          <div className="xl:col-span-1">
            <div className="bg-gray-800 rounded-lg p-4 sm:p-6 border border-gray-700 xl:sticky xl:top-6">
              {/* Promo Image */}
              {challengeSet.promoImageUrl && (
                <div className="mb-6">
                  <img 
                    src={challengeSet.promoImageUrl} 
                    alt={`${challengeSet.title} preview`}
                    className="w-full h-40 object-cover rounded-lg border border-gray-600"
                  />
                </div>
              )}

              {/* Challenge Info */}
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-400">Category</span>
                  <span className="font-medium capitalize">{challengeSet.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Difficulty</span>
                  <span className="font-medium">{challengeSet.difficulty}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Total Challenges</span>
                  <span className="font-medium">{challengeSet.totalChallenges}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Estimated Time</span>
                  <span className="font-medium">{challengeSet.estimatedTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">XP Reward</span>
                  <span className="font-medium text-purple-400">{challengeSet.xpReward}</span>
                </div>
              </div>

              {/* Start Button */}
              <button
                onClick={handleStartChallengeSet}
                className={`w-full bg-gradient-to-r ${styles.gradient} hover:from-blue-700 hover:to-blue-900 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 flex items-center justify-center shadow-lg hover:shadow-blue-500/30`}
              >
                <PlayCircle size={22} className="mr-2" />
                Start Challenge Set
              </button>

              <p className="text-gray-400 text-sm text-center mt-4">
                Complete all {challengeSet.totalChallenges} challenges in sequence to master this topic
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengeOverview;